// State machine for the create-buyer page (VB-B1 persona + VB-B2 questions on
// ONE page, matching the aits-2.0 prototype). The persona form is always
// editable on top; the questions section below is gated until the persona is
// complete. Clicking "Generate questions" creates the buyer under the hood (the
// generate endpoint needs a real buyer id), shows a loading state, then reveals
// the questions. There is no questions-list GET endpoint, so `questions` is a
// local mirror seeded by the generate call and kept in sync on edits.

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Big5Category, Question, SuggestResponse } from "@/types/vb";
import type { AnalyticsSource } from "@/types/analytics";
import {
  countBuyersInCache,
  useAddCompetitor,
  useAddQuestion,
  useCreateBuyer,
  useDeleteBuyer,
  useDeleteCompetitor,
  useDeleteQuestion,
  useGenerateQuestions,
  usePatchBuyer,
  useSuggestBuyer,
  useTriggerAnalysis,
  useUpdateQuestion,
} from "@/hooks/vb/useBuyers";
import { resolveBuyerOrigin, trackVirtualBuyerCreated } from "@/lib/posthog";
import { extractVbError } from "./errors";

// Persona text bounds, mirroring the Buyer model's own constants. The API
// rejects anything outside them, so a bound the backend enforces and the form
// doesn't surfaces as an unexplained failure — keep the two in step.
export const MIN_DESCRIPTION = 50;
export const MAX_DESCRIPTION = 1000;
export const MAX_NAME = 120;
export const MAX_MARKET_CITY = 200;
export const MAX_COMPETITORS = 5;
export const MAX_QUESTIONS = 10;
export const MAX_PER_CATEGORY = 2;

/** Whether the create wizard should show the "company is full" dead end.
 *
 *  The gate asks "may I START a buyer here?", so `hasWork` vetoes it. Without
 *  that veto the wizard evicts its own user: the buyer is created lazily at
 *  "Generate questions", `useCreateBuyer` invalidates `vbKeys.all` — which the
 *  cap hook's buyers list sits under — and the refetch flips `atCap` mid-flow,
 *  swapping the wizard for the dead end and stranding the buyer it just made
 *  with no questions and no run. It also keeps a company that fills up
 *  mid-session from blanking a persona already typed; that case takes the
 *  create's 400 instead, which explains itself and preserves the draft.
 *
 *  Callers must not render the form until the count is known, or a keystroke
 *  during the loading window sets `hasWork` and the dead end can never appear. */
export function wizardIsCapBlocked({
  atCap,
  hasWork,
}: {
  atCap: boolean;
  hasWork: boolean;
}): boolean {
  return atCap && !hasWork;
}

/** Counter copy for the description box, shared by the create wizard and the
 *  edit drawer so the two can't drift apart.
 *
 *  The accepted range is spelled out from the very first render — at length 0
 *  it already reads "0/50 characters minimum (1,000 max)" — so a long paste
 *  can't blow a limit the user was never shown. Over-limit is reported with
 *  the exact overshoot rather than being silently truncated: dropping the tail
 *  of pasted prose loses real content without telling anyone.
 */
export function describeDescriptionLength(len: number): {
  text: string;
  state: "under" | "ok" | "over";
} {
  const max = MAX_DESCRIPTION.toLocaleString();
  if (len > MAX_DESCRIPTION) {
    return {
      state: "over",
      text: `${len.toLocaleString()}/${max} characters — remove ${(
        len - MAX_DESCRIPTION
      ).toLocaleString()}.`,
    };
  }
  if (len < MIN_DESCRIPTION) {
    return {
      state: "under",
      text: `${len}/${MIN_DESCRIPTION} characters minimum (${max} max).`,
    };
  }
  return { state: "ok", text: `${len.toLocaleString()}/${max} characters.` };
}

/** The persona fields Marcus can fill. Drives the "what should I suggest?"
 *  dialog and the merge-only-selected behaviour so a user's manual input is
 *  never silently overwritten. */
export type SuggestTarget = "name" | "description" | "region" | "competitors";
export const SUGGEST_TARGETS: SuggestTarget[] = [
  "name",
  "description",
  "region",
  "competitors",
];

export interface Persona {
  name: string;
  description: string;
  market_region: string;
  market_city: string;
  language: string;
}

export interface CompetitorDraft {
  /** Present once persisted (post-create live CRUD); absent for local drafts. */
  id?: number;
  name: string;
  url: string;
}

const EMPTY_PERSONA: Persona = {
  name: "",
  description: "",
  market_region: "",
  market_city: "",
  language: "English",
};

// tldts carries the full Public Suffix List (~48KB gzip), so we lazy-load it on
// first use (adding a competitor) to keep it out of the initial app bundle - it
// ends up in its own chunk that only loads when someone reaches this field.
let getDomainFn: typeof import("tldts").getDomain | null = null;
async function loadGetDomain() {
  if (!getDomainFn) getDomainFn = (await import("tldts")).getDomain;
  return getDomainFn;
}

/** "https://shop.policygenius.com/foo?x=1" → { name: "Policygenius",
 *  url: "https://policygenius.com" }. tldts strips scheme/www/path/query/port and
 *  reduces any subdomain to the registrable domain via the Public Suffix List, so
 *  multi-part TLDs like acme.co.uk survive and sub.acme.co.uk collapses to it. We
 *  store/analyze that root, and reject junk (bare words, search phrases, IPs) here
 *  against the field the user is editing rather than failing later at generate. */
/** Dedup key: the registrable host without scheme / leading www / trailing
 *  slash, lowercased. So "https://www.marketo.com" and "https://marketo.com"
 *  (e.g. legacy data vs the www-stripping normalizer) compare equal and a
 *  same-competitor duplicate is caught. */
export function competitorUrlKey(url: string): string {
  return url.trim().toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");
}

export async function domainToCompetitor(raw: string): Promise<CompetitorDraft | null> {
  const input = raw.trim().toLowerCase();
  if (!input) return null;
  const getDomain = await loadGetDomain();
  const root = getDomain(input, { allowPrivateDomains: false });
  // Belt-and-suspenders: tldts can be lenient, so still require a clean
  // label(.label)*.tld shape before we accept it.
  if (!root || !/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(root)) return null;
  const label = root.split(".")[0];
  const name = label.charAt(0).toUpperCase() + label.slice(1);
  return { name, url: `https://${root}` };
}

export function useBuyerWizard(
  companyId: number,
  source?: AnalyticsSource,
  companyName?: string | null,
) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [persona, setPersona] = useState<Persona>(EMPTY_PERSONA);
  const [suggestedSnapshot, setSuggestedSnapshot] = useState<SuggestResponse | null>(null);
  const [competitors, setCompetitors] = useState<CompetitorDraft[]>([]);
  const [buyerId, setBuyerId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  // True when the persona has been edited since the questions were generated,
  // so the current questions may no longer match the buyer (drives the nudge).
  const [questionsStale, setQuestionsStale] = useState(false);

  // Error slots, one per surface, so messages render next to their control.
  const [personaError, setPersonaError] = useState<string | null>(null);
  const [competitorError, setCompetitorError] = useState<string | null>(null);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [doneError, setDoneError] = useState<string | null>(null);

  const suggestM = useSuggestBuyer();
  const createM = useCreateBuyer(source);
  const patchM = usePatchBuyer();
  const generateM = useGenerateQuestions(source);
  const addQ = useAddQuestion();
  const updateQ = useUpdateQuestion();
  const deleteQ = useDeleteQuestion();
  const addComp = useAddCompetitor();
  const deleteComp = useDeleteCompetitor();
  const triggerM = useTriggerAnalysis(source);
  const deleteBuyerM = useDeleteBuyer();

  // Synchronous in-flight guards. The button `disabled` flags only take effect
  // after React commits `isPending`, so two clicks in the same tick can both
  // pass the paint-time guard - these refs block the second call immediately.
  const generateLock = useRef(false);
  const ensureBuyerLock = useRef(false);
  const doneLock = useRef(false);
  const suggestLock = useRef(false);
  // Persona edited after the buyer was created → PATCH before generate / run.
  const personaDirty = useRef(false);

  const personaComplete =
    persona.name.trim().length > 0 &&
    persona.name.trim().length <= MAX_NAME &&
    persona.description.trim().length >= MIN_DESCRIPTION &&
    persona.description.trim().length <= MAX_DESCRIPTION &&
    persona.market_region.trim().length > 0 &&
    persona.market_city.trim().length <= MAX_MARKET_CITY;

  const setPersonaField = useCallback(
    <K extends keyof Persona>(key: K, value: Persona[K]) => {
      setPersona((p) => ({ ...p, [key]: value }));
      if (key === "name") setPersonaError(null);
      // Editing the buyer dismisses a stale generate error so the questions
      // section returns to the gate instead of trapping the user on the error
      // card (which only offers "Retry").
      setGenError(null);
      // buyerId != null implies questions were already generated (the buyer is
      // created at generate time), so a persona edit now makes them stale.
      if (buyerId != null) {
        personaDirty.current = true;
        setQuestionsStale(true);
      }
    },
    [buyerId],
  );

  const dismissStale = useCallback(() => setQuestionsStale(false), []);

  /** Set market_region + market_city together (the Places field resolves both
   *  from one pick). Mirrors setPersonaField's dirty/stale bookkeeping. */
  const setLocation = useCallback(
    (region: string, city: string) => {
      setPersona((p) => ({ ...p, market_region: region, market_city: city }));
      setGenError(null);
      if (buyerId != null) {
        personaDirty.current = true;
        setQuestionsStale(true);
      }
    },
    [buyerId],
  );

  const personaScalars = useCallback(
    () => ({
      name: persona.name.trim(),
      description: persona.description.trim(),
      market_region: persona.market_region.trim(),
      market_city: persona.market_city.trim(),
      language: persona.language || "English",
    }),
    [persona],
  );

  // --- Suggest -------------------------------------------------------------
  // ``targets`` selects which fields Marcus is allowed to overwrite; only those
  // are merged in, so a user's manual input is preserved. Omitting it suggests
  // everything (the fresh-form one-click path). The user's current draft rides
  // along so the suggestion is built AROUND their input (and competitors are
  // biased toward their persona, not an LLM-invented one).
  const suggest = useCallback(async (targets?: SuggestTarget[]) => {
    // Don't let a re-suggest race a generate-in-flight: while the buyer is being
    // created, this closure still sees buyerId == null and would clobber the
    // just-persisted competitors with id-less drafts and skip the stale flag.
    if (suggestLock.current || generateLock.current) return;
    const sel = targets && targets.length ? targets : SUGGEST_TARGETS;
    const want = (t: SuggestTarget) => sel.includes(t);
    suggestLock.current = true;
    setPersonaError(null);
    try {
      const s = await suggestM.mutateAsync({
        companyId,
        draft: {
          name: persona.name.trim(),
          description: persona.description.trim(),
          market_region: persona.market_region.trim(),
          market_city: persona.market_city.trim(),
        },
      });
      // Merge only the selected fields; keep whatever the user typed otherwise.
      setPersona((p) => ({
        ...p,
        name: want("name") ? (s.name ?? p.name) : p.name,
        description: want("description") ? (s.description ?? p.description) : p.description,
        market_region: want("region") ? (s.market_region ?? p.market_region) : p.market_region,
        market_city: want("region") ? (s.market_city ?? p.market_city) : p.market_city,
        language: s.language || p.language,
      }));
      setSuggestedSnapshot(s);
      if (buyerId != null) {
        personaDirty.current = true;
        setQuestionsStale(true);
      }
      // Pre-fill suggested competitors too (best-effort; may be empty). Only
      // pre-create - after the buyer exists, competitors are live CRUD and must
      // not be clobbered by a re-suggest.
      if (want("competitors") && buyerId == null && Array.isArray(s.competitors)) {
        setCompetitors(
          s.competitors.slice(0, MAX_COMPETITORS).map((c) => ({ name: c.name, url: c.url })),
        );
      }
    } catch (err) {
      const e = extractVbError(err);
      // The suggest endpoint reuses the company-inference error map, so an
      // upstream LLM hiccup arrives as CANNOT_INFER / "couldn't analyze this
      // URL" - misleading here (there's no URL field). Show a clear, retryable
      // message with the manual fallback; keep throttle copy as-is.
      setPersonaError(
        e.code === "CONTEXT_MISSING"
          ? "This company hasn't been analyzed yet. Run its analysis before suggesting a buyer."
          : e.code === "THROTTLED"
            ? e.detail
            : "Marcus couldn't draft a persona just now. Try again, or fill the fields in manually.",
      );
    } finally {
      suggestLock.current = false;
    }
  }, [companyId, suggestM, buyerId, persona]);

  // --- Competitors ---------------------------------------------------------
  const addCompetitor = useCallback(
    async (raw: string) => {
      setCompetitorError(null);
      let draft: CompetitorDraft | null;
      try {
        draft = await domainToCompetitor(raw);
      } catch {
        // The normalizer lazy-imports tldts; that import can reject offline or
        // when a stale chunk hash 404s after a redeploy. Surface it instead of
        // silently dropping the user's input (and an unhandled rejection).
        setCompetitorError("Couldn't check that domain just now. Please try again.");
        return;
      }
      if (!draft) {
        setCompetitorError("Enter a valid domain, e.g. competitor.com");
        return;
      }
      if (competitors.length >= MAX_COMPETITORS) {
        setCompetitorError(`Up to ${MAX_COMPETITORS} competitors.`);
        return;
      }
      if (competitors.some((c) => competitorUrlKey(c.url) === competitorUrlKey(draft.url))) {
        setCompetitorError("That competitor is already added.");
        return;
      }
      if (buyerId == null) {
        setCompetitors((cs) => [...cs, draft]);
        return;
      }
      // Post-create: persist immediately via live CRUD.
      try {
        const saved = await addComp.mutateAsync({ buyerId, competitor: draft });
        setCompetitors((cs) => [...cs, { id: saved.id, name: saved.name, url: saved.url }]);
      } catch (err) {
        const e = extractVbError(err);
        setCompetitorError(
          e.code === "DUPLICATE_URL"
            ? "That competitor is already added."
            : e.code === "TOTAL_CAP_REACHED"
              ? `Up to ${MAX_COMPETITORS} competitors.`
              : e.detail,
        );
      }
    },
    [competitors, buyerId, addComp],
  );

  const removeCompetitor = useCallback(
    async (target: CompetitorDraft) => {
      setCompetitorError(null);
      if (target.id != null) {
        try {
          await deleteComp.mutateAsync(target.id);
        } catch (err) {
          setCompetitorError(extractVbError(err).detail);
          return;
        }
      }
      // Filter by url - the list's unique key - not object identity.
      setCompetitors((cs) => cs.filter((c) => c.url !== target.url));
    },
    [deleteComp],
  );

  // Ensure buyer row exists and is up-to-date with the persona fields.
  const ensureBuyer = useCallback(async (): Promise<number | null> => {
    if (!personaComplete || ensureBuyerLock.current || suggestLock.current) return null;
    ensureBuyerLock.current = true;
    setPersonaError(null);
    const scalars = personaScalars();
    try {
      let bid = buyerId;
      if (bid == null) {
        const buyer = await createM.mutateAsync({
          company_id: companyId,
          ...scalars,
          suggested_snapshot: suggestedSnapshot,
          competitors: competitors.map((c) => ({ name: c.name, url: c.url })),
        });
        bid = buyer.id;
        setBuyerId(bid);
        // Adopt server-assigned competitor ids so later edits use live CRUD.
        setCompetitors(
          (buyer.competitors ?? []).map((c) => ({ id: c.id, name: c.name, url: c.url })),
        );
        personaDirty.current = false;
      } else if (personaDirty.current) {
        await patchM.mutateAsync({ buyerId: bid, partial: scalars });
        personaDirty.current = false;
      }
      return bid;
    } catch (err) {
      const e = extractVbError(err);
      // COMPANY_BUYER_CAP_REACHED needs no case of its own: the wizard blocks a
      // full company on entry, so reaching here means someone else in the
      // workspace took the last slot while this form was open, and the backend's
      // own copy already names the company and the limit. Falls through to
      // `e.detail` rather than being paraphrased into something vaguer.
      setPersonaError(
        e.code === "DUPLICATE_NAME"
          ? "You already have a buyer with this name for this company."
          : e.detail,
      );
      return null;
    } finally {
      ensureBuyerLock.current = false;
    }
  }, [
    personaComplete,
    personaScalars,
    buyerId,
    companyId,
    suggestedSnapshot,
    competitors,
    createM,
    patchM,
  ]);

  // --- Generate questions (creates the buyer on first run) -----------------
  // One action drives the initial generate, regenerate, and retry. It ensures
  // the buyer exists (create, or PATCH persona edits) then replaces the set.
  const generateQuestions = useCallback(async () => {
    if (!personaComplete || generateLock.current || suggestLock.current) return;
    generateLock.current = true;
    setPersonaError(null);
    setGenError(null);
    try {
      const bid = await ensureBuyer();
      if (bid == null) return;
      // Phase 2 - generate (replace-all). Buyer exists now.
      try {
        const qs = await generateM.mutateAsync(bid);
        setQuestions(qs);
        setQuestionsStale(false); // fresh set now matches the current persona
      } catch (err) {
        const e = extractVbError(err);
        setGenError(
          e.code === "CONTEXT_MISSING"
            ? "This company hasn't been analyzed yet. Its context is needed to generate questions."
            : e.code === "THROTTLED"
              ? e.detail
              : "We couldn't generate the questions just now. Try again.",
        );
      }
    } finally {
      generateLock.current = false;
    }
  }, [
    personaComplete, ensureBuyer, generateM,
  ]);

  // --- Question edits ------------------------------------------------------
  const perCategoryCount = useCallback(
    (category: Big5Category) => questions.filter((q) => q.category === category).length,
    [questions],
  );

  const addManualQuestion = useCallback(
    async (category: Big5Category, text: string) => {
      setQuestionError(null);
      const trimmed = text.trim();
      if (!trimmed || buyerId == null) return;
      if (questions.length >= MAX_QUESTIONS) {
        setQuestionError(`Up to ${MAX_QUESTIONS} questions total. Remove one first.`);
        return;
      }
      if (perCategoryCount(category) >= MAX_PER_CATEGORY) {
        setQuestionError(`Up to ${MAX_PER_CATEGORY} questions per topic. Remove one first.`);
        return;
      }
      try {
        const q = await addQ.mutateAsync({ buyerId, category, text: trimmed });
        setQuestions((qs) => [...qs, q]);
      } catch (err) {
        const e = extractVbError(err);
        setQuestionError(
          e.code === "CATEGORY_CAP_REACHED"
            ? `Up to ${MAX_PER_CATEGORY} questions per topic. Remove one first.`
            : e.code === "TOTAL_CAP_REACHED"
              ? `Up to ${MAX_QUESTIONS} questions total. Remove one first.`
              : e.detail,
        );
      }
    },
    [buyerId, questions, perCategoryCount, addQ],
  );

  const editQuestion = useCallback(
    async (questionId: string, text: string) => {
      setQuestionError(null);
      const trimmed = text.trim();
      if (!trimmed) return;
      try {
        const updated = await updateQ.mutateAsync({ questionId, text: trimmed });
        setQuestions((qs) => qs.map((q) => (q.id === questionId ? updated : q)));
      } catch (err) {
        setQuestionError(extractVbError(err).detail);
      }
    },
    [updateQ],
  );

  const removeQuestion = useCallback(
    async (questionId: string) => {
      setQuestionError(null);
      try {
        await deleteQ.mutateAsync(questionId);
        setQuestions((qs) => qs.filter((q) => q.id !== questionId));
      } catch (err) {
        setQuestionError(extractVbError(err).detail);
      }
    },
    [deleteQ],
  );

  // --- Provenance (AITS-545 QA) -------------------------------------------
  // buyer_origin = did the user change Marcus's suggestion? "edited" if a persona
  // field OR any generated question differs; "custom" with no suggestion at all.
  // Computed from live wizard state (mirrors the backend edited_fields() diff for
  // persona) and consumed at finalization, so post-generate persona tweaks and
  // question edits both count — the create-time event never saw them.
  const normField = (s?: string | null) => (s ?? "").trim();
  const personaEdited =
    suggestedSnapshot != null &&
    (normField(persona.name) !== normField(suggestedSnapshot.name) ||
      normField(persona.description) !== normField(suggestedSnapshot.description) ||
      normField(persona.language) !== normField(suggestedSnapshot.language) ||
      normField(persona.market_region) !== normField(suggestedSnapshot.market_region) ||
      normField(persona.market_city) !== normField(suggestedSnapshot.market_city));
  const questionsEdited = questions.some((q) => q.source !== "GENERATED");
  const buyerOrigin = resolveBuyerOrigin({ suggestedSnapshot, personaEdited, questionsEdited });

  // --- Finish / cancel -----------------------------------------------------
  const done = useCallback(async () => {
    setDoneError(null);
    if (buyerId == null || questions.length === 0) {
      setDoneError("Generate the questions before running the simulation.");
      return;
    }
    if (doneLock.current) return;
    doneLock.current = true;
    try {
      // Persist any persona edits made after the last generate so the run uses
      // the current persona. If the edit is rejected (e.g. a now-duplicate
      // name), surface it and stop - don't silently run a persona the user
      // thinks they changed.
      if (personaDirty.current) {
        try {
          await patchM.mutateAsync({ buyerId, partial: personaScalars() });
          personaDirty.current = false;
        } catch (err) {
          const e = extractVbError(err);
          setDoneError(
            e.code === "DUPLICATE_NAME"
              ? "You already have a buyer with this name for this company."
              : `Couldn't save your buyer changes: ${e.detail}`,
          );
          return;
        }
      }
      // Buyer is finalized now (persona flushed, questions set) — emit
      // virtual_buyer_created with its real provenance, before run_started.
      // Deduped per buyer, so a 409 retry below can't double-count it.
      trackVirtualBuyerCreated({
        buyerId,
        origin: buyerOrigin,
        companyId,
        companyName,
        virtualBuyerCount: Math.max(countBuyersInCache(qc), 1),
        source,
      });
      const shadow = await triggerM.mutateAsync({ buyerId, questionsCount: questions.length });
      // `?new=1` marks this as the create-wizard's "Run simulation" step so the
      // run screen shows the 3-step progress (step 2 of 3), per the aits-2.0
      // flow. A re-run from the buyer detail page omits it (no wizard chrome).
      navigate(`/virtual-buyer/${buyerId}/run/${shadow.id}?new=1`);
    } catch (err) {
      const e = extractVbError(err);
      // 409: a run is already in flight - jump to it rather than erroring.
      if (e.status === 409) {
        const existingId = (err as { response?: { data?: { existingAnalysisId?: number } } })
          .response?.data?.existingAnalysisId;
        if (existingId != null) {
          navigate(`/virtual-buyer/${buyerId}/run/${existingId}?new=1`);
          return;
        }
      }
      setDoneError(
        e.code === "NO_QUESTIONS"
          ? "Add at least one question before running the simulation."
          : e.detail,
      );
    } finally {
      doneLock.current = false;
    }
  }, [
    buyerId, questions.length, personaScalars, patchM, triggerM, navigate,
    buyerOrigin, companyId, companyName, source, qc,
  ]);

  const cancel = useCallback(async () => {
    // A run is being triggered (buyer + shadow analysis created). Deleting the
    // buyer now would cascade-delete that in-flight analysis and 404 the run we
    // are about to navigate to, so let `done()` finish instead.
    if (doneLock.current) return;
    if (buyerId != null) {
      try {
        await deleteBuyerM.mutateAsync(buyerId);
      } catch {
        // Best-effort orphan cleanup - navigate regardless.
      }
    }
    navigate("/virtual-buyer");
  }, [buyerId, deleteBuyerM, navigate]);

  const generating = createM.isPending || generateM.isPending;

  return useMemo(
    () => ({
      persona,
      setPersonaField,
      setLocation,
      personaComplete,
      // Any user effort worth confirming before discarding on Cancel.
      hasWork:
        buyerId != null ||
        questions.length > 0 ||
        competitors.length > 0 ||
        persona.name.trim().length > 0 ||
        persona.description.trim().length > 0 ||
        persona.market_region.trim().length > 0,
      personaError,
      suggest,
      suggesting: suggestM.isPending,
      competitors,
      addCompetitor,
      removeCompetitor,
      competitorError,
      addingCompetitor: addComp.isPending,
      buyerId,
      questions,
      questionCount: questions.length,
      hasQuestions: questions.length > 0,
      ensureBuyer,
      generateQuestions,
      generating,
      genError,
      questionsStale,
      dismissStale,
      hasEdits: questionsEdited,
      /** suggested / edited / custom for the current wizard state (AITS-545). */
      buyerOrigin,
      addManualQuestion,
      addingQuestion: addQ.isPending,
      editQuestion,
      removeQuestion,
      questionError,
      done,
      finishing: triggerM.isPending,
      doneError,
      cancel,
    }),
    [
      persona, setPersonaField, setLocation, personaComplete, personaError, suggest, suggestM.isPending,
      competitors, addCompetitor, removeCompetitor, competitorError, addComp.isPending, buyerId,
      questions, ensureBuyer, generateQuestions, generating, genError, questionsStale, dismissStale,
      addManualQuestion, addQ.isPending, editQuestion, removeQuestion, questionError, done,
      triggerM.isPending, doneError, cancel, buyerOrigin, questionsEdited,
    ],
  );
}

export type BuyerWizard = ReturnType<typeof useBuyerWizard>;
